import React, { useContext, useState, useRef } from "react";
import minus from "../../assets/icons/minus.png";
import add from "../../assets/icons/add.png";
import dots from "../../assets/icons/dots.png";
import "./Navbar.css";
import { FaBars, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { StudyContext } from "../../context/studyData/studyData";
import DarkModeToggle from "../darkmodetoogle/DarkModeToggle";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { usePatientDetails } from "../../context/patientDetails/patientDetails";
import { updateUserStudy } from "../../utils/API_SERVICE_FOR_PATIENT_PAGE";
import { formatDateForInput, formatDateForDisplay } from "../../utils/helper";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LanguageDropdown from "../common/LanguageDropdown";
import { useTranslation } from "react-i18next";
import { useI18nLanguage } from "../../i18n/hooks/useI18nLanguage";
import { downloadDicomStructuredReportHtml } from "../../utils/API_SERVICE";
import DsrModal from "../common/DsrModal";
import {
  CreOrganisationHeaderProvider,
  CreOrganisationHeaderModals,
  CreOrganisationHeaderChip,
} from "../../cre/CreOrganisationHeader";
import { UI_LANGUAGES } from "../../constants/languages";
import CompanionAgentHeaderSettings from "../agent/CompanionAgentHeaderSettings";
import { NAVBAR_ICON_BUTTON_CLASS } from "./navbarIconButtonClasses";
import { useUserUiPreference } from "../../Hooks/useUserUiPreference";
import { UI_PREFERENCE } from "../../utils/userUiPreferences";

function Navbar({ setSidebarTranscript, language: currentLanguage, setLanguage }) {
  const { t } = useTranslation("common");
  const { isRTL } = useI18nLanguage(); // Use isRTL from hook

  // Font size constants - change DEFAULT_FONT_SIZE here to update everywhere
  const DEFAULT_FONT_SIZE = 18;
  const MAX_FONT_CHANGES = 5; // Maximum 5 increases or decreases
  const MAX_FONT_SIZE = DEFAULT_FONT_SIZE + MAX_FONT_CHANGES * 2; // 18 + 10 = 28px
  const MIN_FONT_SIZE = Math.max(10, DEFAULT_FONT_SIZE - MAX_FONT_CHANGES * 2); // max(10, 8) = 10px

  const [menuOpen, setMenuOpen] = useState(false);
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const toggleMenu = () => setMenuOpen(!menuOpen);
  const [dsrOpen, setDsrOpen] = useState(false);
  const [dsrLoading, setDsrLoading] = useState(false);
  const [dsrError, setDsrError] = useState("");
  const [dsrHtml, setDsrHtml] = useState("");
  // Per-user FE setting: collapse the patient fields row (persists in localStorage).
  const [patientDetailsHidden, setPatientDetailsHidden] = useUserUiPreference(
    UI_PREFERENCE.PATIENT_DETAILS_HIDDEN,
    false,
  );
  const patientDetailsToggleLabel = patientDetailsHidden
    ? t("showPatientDetails")
    : t("hidePatientDetails");

  const handleLanguageChange = (newLang) => {
    // changeLanguage(newLang);
    setLanguage(newLang);
  };
  const textDirection = isRTL ? "rtl" : "ltr";
  const { studyData, setStudyData: setGlobalStudyData } = useContext(
    StudyContext,
  );
  const { isCreAuth, isRadicalAuth } = usePatientAuthFlags();
  const isLoading = !studyData && !isCreAuth;

  const {
    patientDetails,
    isEditing,
    isSaving,
    updatePatientDetails,
    resetPatientDetails,
    setAllPatientDetails,
    setIsEditing,
    setIsSaving,
  } = usePatientDetails();
  const [menuDropdownOpen, setMenuDropdownOpen] = useState(false);
  const menuDropdownRef = useRef(null);

  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(event.target)
      ) {
        setMenuDropdownOpen(false);
      }
    }
    if (menuDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuDropdownOpen]);

  React.useEffect(() => {
    if (studyData) {
      setAllPatientDetails({
        patientId: studyData?.patientId || "",
        patientName: studyData?.patientName || "",
        accessionNo: studyData?.accessionNo || "",
        dateOfBirth: studyData?.dateOfBirth
          ? formatDateForInput(studyData?.dateOfBirth)
          : "",
        modalityCode: studyData?.modalityCode || "",
        patientSex: studyData?.patientSex || "",
        insuranceName: studyData?.insuranceName || "",
        insuranceNumber: studyData?.insuranceNumber || "",
        weight: studyData?.weight || "",
      });
    }
  }, [studyData, setAllPatientDetails]);

  const handleFieldChange = (field, value) => {
    updatePatientDetails(field, value);
  };

  const handleSave = async () => {
    if (!studyData?.studyId) return;
    try {
      setIsSaving(true);
      const updateObj = {
        studyId: studyData.studyId,
        firstName: patientDetails?.patientName
          ? patientDetails.patientName.split(" ")[0]
          : "",
        lastName: patientDetails?.patientName
          ? patientDetails.patientName
              .split(" ")
              .slice(1)
              .join(" ")
          : "",
        accNo: patientDetails?.accessionNo,
        dateOfBirth: patientDetails?.dateOfBirth,
        gender: patientDetails?.patientSex,
        mrn: patientDetails?.modalityCode,
        insuranceName: patientDetails?.insuranceName,
        insuranceNumber: patientDetails?.insuranceNumber,
        weight: patientDetails?.weight || 0,
        patientId: patientDetails?.patientId,
      };
      const latest = await updateUserStudy(updateObj);
      if (latest) {
        setIsEditing(false);
        setGlobalStudyData(latest);
        toast.success(t("patientInfoUpdated"));
      } else {
        toast.error(t("failedToUpdatePatientInfo"));
      }
    } catch (err) {
      toast.error(t("errorUpdatingPatientInfo"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    // Reset patient details to original values
    resetPatientDetails();
    if (setSidebarTranscript) {
      setSidebarTranscript("");
    }
  };

  const handleShowDsr = async () => {
    try {
      const accessionNumber = patientDetails?.accessionNo || "";
      if (!String(accessionNumber).trim()) {
        toast.error("Please enter accession number");
        return;
      }
      setDsrOpen(true);
      setDsrLoading(true);
      setDsrError("");
      setDsrHtml("");
      const data = await downloadDicomStructuredReportHtml(accessionNumber);
      const html = data?.reportHtml ? String(data.reportHtml) : "";
      if (!html.trim()) {
        throw new Error("Missing reportHtml in response");
      }
      setDsrHtml(html);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load DSR";
      setDsrError(String(msg));
    } finally {
      setDsrLoading(false);
    }
  };

  const patientInfo = [
    {
      label: t("patientName"),
      value: isLoading ? "—" : studyData?.patientName,
      field: "patientName",
    },
    {
      label: t("accNumber"),
      value: isLoading ? "—" : studyData?.accessionNo,
      field: "accessionNo",
    },
    {
      label: t("dob"),
      value: isLoading
        ? "—"
        : studyData?.dateOfBirth
        ? formatDateForDisplay(studyData.dateOfBirth)
        : "—",
      field: "dateOfBirth",
    },
    {
      label: t("modality"),
      value: isLoading ? "—" : studyData?.modalityCode,
      field: "modalityCode",
    },
    {
      label: t("gender"),
      value: isLoading ? "—" : studyData?.patientSex,
      field: "patientSex",
    },
  ];

  // Extra editable fields for CRE users
  const extraCreFields = [
    {
      label: t("patientId"),
      value: isLoading ? "—" : studyData?.patientId,
      field: "patientId",
    },
    {
      label: t("insuranceName"),
      value: isLoading ? "—" : studyData?.insuranceName,
      field: "insuranceName",
    },
    {
      label: t("insuranceNumber"),
      value: isLoading ? "—" : studyData?.insuranceNumber,
      field: "insuranceNumber",
    },
    {
      label: t("weight"),
      value: isLoading ? "—" : studyData?.weight,
      field: "weight",
    },
  ];

  const allPatientInfo = isCreAuth
    ? [...patientInfo, ...extraCreFields]
    : patientInfo;

  // Increase font size (max 5 increases from default = +10px)
  const increaseFontSize = () => {
    if (fontSize < MAX_FONT_SIZE) {
      setFontSize((prev) => Math.min(prev + 2, MAX_FONT_SIZE));
    }
  };

  // Decrease font size (max 5 decreases from default, minimum 10px)
  const decreaseFontSize = () => {
    if (fontSize > MIN_FONT_SIZE) {
      setFontSize((prev) => Math.max(prev - 2, MIN_FONT_SIZE));
    }
  };

  // Check if buttons should be disabled
  const canIncrease = fontSize < MAX_FONT_SIZE;
  const canDecrease = fontSize > MIN_FONT_SIZE;

  if (isRadicalAuth) {
    return null;
  }

  return (
    <CreOrganisationHeaderProvider>
    <div
      className="w-full rounded-md place-content-center items-center p-2 sm:p-3 flex justify-between relative min-w-0 max-w-full bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700"
      style={{ overflow: "visible" }}
    >
      <CreOrganisationHeaderModals />
      <DsrModal
        open={dsrOpen}
        onClose={() => setDsrOpen(false)}
        loading={dsrLoading}
        error={dsrError}
        html={dsrHtml}
      />
      <div className="lg:hidden flex items-center w-full gap-2 min-w-0 p-1 sm:p-2 z-10">
        <button
          onClick={toggleMenu}
          className="text-lg sm:text-xl bg-gray-200 dark:bg-gray-700 rounded-full p-2.5 sm:p-2 flex items-center justify-center flex-shrink-0 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors min-w-[44px] min-h-[44px]"
          aria-label="Toggle menu"
          type="button"
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
        {isCreAuth ? (
          <div className="ml-auto flex items-center gap-2 flex-shrink-0">
            {isCreAuth ? <CreOrganisationHeaderChip /> : null}
            <CompanionAgentHeaderSettings />
          </div>
        ) : null}
      </div>
      {/* Backdrop overlay for mobile menu */}
      {menuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[99]"
          onClick={toggleMenu}
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        />
      )}
      {/* Grid Section - Show in Burger Menu for Small Screens */}
      {menuOpen && (
        <div
          className="lg:hidden bg-gray-50 dark:bg-gray-700 rounded-md shadow-lg p-3 sm:p-4 absolute top-full left-0 right-0 w-full z-[100] mt-1 border border-gray-200 dark:border-gray-600"
          style={{ overflow: "visible" }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu();
            }}
            className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-700 rounded-full p-2 sm:p-2.5 z-[150] flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer"
            style={{
              minWidth: "44px",
              minHeight: "44px",
              pointerEvents: "auto",
            }}
            aria-label="Close menu"
            type="button"
          >
            <FaTimes size={20} className="sm:w-6 sm:h-6 pointer-events-none" />
          </button>

          {/* Buttons Section - Mobile. CRE: LanguageDropdown + Save + Reset below. */}
          <div
            className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center w-full mb-4 mt-2"
            style={{ position: "relative", zIndex: 101 }}
          >
            <div
              className="w-full sm:w-auto min-w-[120px] relative"
              style={{ zIndex: 200 }}
            >
              <LanguageDropdown
                value={currentLanguage}
                onChange={handleLanguageChange}
                languages={UI_LANGUAGES}
              />
            </div>
            {isCreAuth && (
              <>
                <button
                  className="border bg-[#A4C639] h-11 sm:h-10 rounded-full px-4 sm:px-5 font-medium text-gray-800 hover:bg-[#CBEA7B80] disabled:hover:bg-[#A4C639] transition-colors disabled:opacity-50 text-xs sm:text-sm whitespace-nowrap min-w-[44px]"
                  onClick={handleSave}
                  disabled={!isEditing || isSaving}
                >
                  {isSaving ? t("saving") : t("save")}
                </button>
                <button
                  className="border bg-[#A4C639] h-11 sm:h-10 rounded-full px-4 sm:px-5 font-medium text-gray-800 hover:bg-[#CBEA7B80] disabled:hover:bg-[#A4C639] transition-colors disabled:opacity-50 text-xs sm:text-sm whitespace-nowrap min-w-[44px]"
                  onClick={handleReset}
                  disabled={isSaving}
                >
                  {t("reset")}
                </button>
              </>
            )}
            {/* Dark Mode Toggle and Three Dots next to Reset button */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 flex-wrap">
              <DarkModeToggle />
              {/* Three Dots Dropdown for Mobile */}
              <div className="relative flex-shrink-0 z-[100]">
                <button
                  type="button"
                  className={NAVBAR_ICON_BUTTON_CLASS}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setMenuDropdownOpen((open) => !open);
                  }}
                  aria-label="Menu options"
                >
                  <img
                    src={dots}
                    alt=""
                    className="w-1 h-3 sm:h-4 dark:invert pointer-events-none"
                  />
                </button>

                {menuDropdownOpen && (
                  <div
                    ref={menuDropdownRef}
                    className="absolute left-0 mt-2 w-36 sm:w-40 bg-white dark:bg-gray-800 rounded shadow-lg z-[200] p-2 flex flex-col gap-2 min-w-[9rem]"
                    style={{ pointerEvents: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        decreaseFontSize();
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      disabled={!canDecrease}
                      className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm transition-all"
                    >
                      <img
                        src={minus}
                        alt="Decrease font"
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 dark:invert flex-shrink-0 pointer-events-none"
                      />
                      <span>{t("decreaseFont")}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        increaseFontSize();
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      disabled={!canIncrease}
                      className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm transition-all"
                    >
                      <img
                        src={add}
                        alt="Increase font"
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 dark:invert flex-shrink-0 pointer-events-none"
                      />
                      <span>{t("increaseFont")}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Patient Info - Mobile. CRE: full grid + editable fields. Radical: compact list + read-only. */}
          <div className="max-h-[60vh] overflow-y-auto overflow-x-auto mobile-scroll">
            <div
              className={`grid gap-2 sm:gap-3 items-center justify-items-start ${
                isCreAuth
                  ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
                  : "grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2"
              }`}
            >
              {allPatientInfo.map((info, index) => (
                <div
                  className={`flex w-full min-w-0 ${
                    !isCreAuth
                      ? "flex-row items-baseline gap-2 py-1.5 border-b border-gray-200 dark:border-gray-600 last:border-0"
                      : "flex-col"
                  }`}
                  key={index}
                >
                  <h1
                    className={`text-gray-900 dark:text-gray-200 w-full min-w-0 ${
                      !isCreAuth
                        ? "flex flex-row items-baseline gap-2 flex-wrap"
                        : ""
                    }`}
                  >
                    <p
                      className={`text-[10px] sm:text-xs ${
                        !isCreAuth
                          ? "text-gray-500 dark:text-gray-400 font-medium shrink-0 mb-0"
                          : "w-full mb-0.5 sm:mb-1"
                      }`}
                    >
                      {info.label}:{" "}
                    </p>
                    {isCreAuth ? (
                      info.field === "dateOfBirth" ? (
                        <input
                          type="date"
                          dir={textDirection}
                          className="w-full min-w-0 rounded-md px-1.5 sm:px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 font-bold focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-150 shadow-sm text-[10px] sm:text-xs"
                          style={{
                            fontSize: `clamp(10px, ${fontSize}px, 32px)`,
                            minWidth: "120px",
                          }}
                          value={patientDetails[info.field] || ""}
                          onChange={(e) =>
                            handleFieldChange(info.field, e.target.value)
                          }
                        />
                      ) : info.field === "patientSex" ? (
                        <select
                          dir={textDirection}
                          className="w-full min-w-0 rounded-md px-1.5 sm:px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 font-bold focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-150 shadow-sm text-[10px] sm:text-xs"
                          style={{
                            fontSize: `clamp(10px, ${fontSize}px, 32px)`,
                            minWidth: "70px",
                          }}
                          value={patientDetails[info.field] || ""}
                          onChange={(e) =>
                            handleFieldChange(info.field, e.target.value)
                          }
                        >
                          <option value="" disabled>
                            Select
                          </option>
                          <option value="M">M</option>
                          <option value="F">F</option>
                        </select>
                      ) : info.field === "weight" ? (
                        <input
                          type="number"
                          min="0"
                          step="any"
                          dir={textDirection}
                          className="w-full min-w-0 rounded-md px-1.5 sm:px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 font-bold focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-150 shadow-sm text-[10px] sm:text-xs"
                          style={{
                            fontSize: `clamp(10px, ${fontSize}px, 32px)`,
                            minWidth: "60px",
                          }}
                          value={patientDetails[info.field] || ""}
                          onChange={(e) =>
                            handleFieldChange(info.field, e.target.value)
                          }
                        />
                      ) : (
                      <div className="flex items-center gap-2 w-full min-w-0">
                        <input
                          dir={textDirection}
                          className="w-full min-w-0 rounded-md px-1.5 sm:px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 font-bold focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-150 shadow-sm text-[10px] sm:text-xs"
                          style={{
                            fontSize: `clamp(10px, ${fontSize}px, 32px)`,
                            minWidth:
                              info.field === "insuranceName" ||
                              info.field === "insuranceNumber"
                                ? "180px"
                                : "100px",
                          }}
                          value={patientDetails[info.field] || ""}
                          onChange={(e) =>
                            handleFieldChange(info.field, e.target.value)
                          }
                        />
                        {info.field === "accessionNo" && (
                          <button
                            type="button"
                            onClick={handleShowDsr}
                            className="h-8 px-3 rounded-full bg-[#A4C639] hover:bg-[#CBEA7B80] text-[10px] sm:text-xs font-medium text-gray-800 whitespace-nowrap"
                          >
                            Show DSR
                          </button>
                        )}
                      </div>
                      )
                    ) : (
                      <div className="flex items-center gap-2 w-full min-w-0">
                        <span
                          className={`font-bold text-[10px] sm:text-xs break-words ${
                            isLoading ? "opacity-60 italic" : ""
                          }`}
                          style={{
                            fontSize: `${fontSize}px`,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            maxWidth: "100%",
                          }}
                        >
                          {info.value}
                        </span>
                        {info.field === "accessionNo" && (
                          <button
                            type="button"
                            onClick={handleShowDsr}
                            className="h-8 px-3 rounded-full bg-[#A4C639] hover:bg-[#CBEA7B80] text-[10px] sm:text-xs font-medium text-gray-800 whitespace-nowrap"
                          >
                            Show DSR
                          </button>
                        )}
                      </div>
                    )}
                  </h1>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Desktop (lg+). CRE: title + editable grid below. Radical: title + compact strip. */}
      <div
        className="hidden lg:flex flex-col gap-1 place-content-center px-1 xl:px-2 w-full min-w-0 max-w-full"
        style={{ overflow: "visible" }}
      >
        <div className="flex flex-row justify-between items-center w-full gap-2 min-w-0 flex-wrap">
          <div className="flex flex-1 min-w-0 items-center gap-2">
            <h1
              className="font-bold min-w-0 text-sm xl:text-base 2xl:text-lg text-gray-700 dark:text-gray-300"
              style={{ fontSize: `clamp(14px, ${fontSize + 5}px, 40px)` }}
            >
              {t("patientDetails")}
            </h1>
            <button
              type="button"
              onClick={() => setPatientDetailsHidden(!patientDetailsHidden)}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              title={patientDetailsToggleLabel}
              aria-label={patientDetailsToggleLabel}
              aria-expanded={!patientDetailsHidden}
            >
              {patientDetailsHidden ? (
                <FaChevronDown className="h-3.5 w-3.5" />
              ) : (
                <FaChevronUp className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          <div className="flex flex-row gap-1 sm:gap-2 place-content-center items-center flex-shrink-0 flex-wrap justify-end">
            <div className="flex gap-1 sm:gap-2 items-center flex-wrap justify-end">
              {isCreAuth ? <CreOrganisationHeaderChip /> : null}
              <div
                className="min-w-[100px] xl:min-w-[120px] relative"
                style={{ zIndex: 100 }}
              >
                <LanguageDropdown
                  value={currentLanguage}
                  onChange={handleLanguageChange}
                  languages={UI_LANGUAGES}
                />
              </div>
              {isCreAuth && (
                <>
                  <button
                    className="border bg-[#A4C639] h-10 xl:h-10 rounded-full px-3 xl:px-5 font-medium text-gray-800 hover:bg-[#CBEA7B80] disabled:hover:bg-[#A4C639] transition-colors disabled:opacity-50 text-xs xl:text-sm whitespace-nowrap min-h-[44px]"
                    onClick={handleSave}
                    disabled={!isEditing || isSaving}
                  >
                    {isSaving ? t("saving") : t("save")}
                  </button>
                  <button
                    className="border bg-[#A4C639] h-10 xl:h-10 rounded-full px-3 xl:px-5 font-medium text-gray-800 hover:bg-[#CBEA7B80] disabled:hover:bg-[#A4C639] transition-colors disabled:opacity-50 text-xs xl:text-sm whitespace-nowrap min-h-[44px]"
                    onClick={handleReset}
                    disabled={isSaving}
                  >
                    {t("reset")}
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 sm:gap-2 px-1 sm:px-2 py-1 flex-shrink-0 flex-wrap">
              <DarkModeToggle />
            </div>
            {/* Three Dots - Desktop */}
            <div className="relative flex-shrink-0 z-[150]">
              <button
                type="button"
                className={NAVBAR_ICON_BUTTON_CLASS}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuDropdownOpen((open) => !open);
                }}
                aria-label="Menu options"
              >
                <img src={dots} alt="" className="w-1 h-3 xl:h-4 dark:invert" />
              </button>

              {menuDropdownOpen && (
                <div
                  ref={menuDropdownRef}
                  className="absolute right-0 mt-2 w-36 xl:w-40 bg-white dark:bg-gray-800 rounded shadow-lg z-[200] p-2 flex flex-col gap-2"
                  style={{ pointerEvents: "auto" }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      decreaseFontSize();
                    }}
                    disabled={!canDecrease}
                    className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed text-xs xl:text-sm transition-all"
                  >
                    <img
                      src={minus}
                      alt="Decrease font"
                      className="w-3 h-3 xl:w-3.5 xl:h-3.5 dark:invert flex-shrink-0"
                    />
                    <span>Decrease Font</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      increaseFontSize();
                    }}
                    disabled={!canIncrease}
                    className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed text-xs xl:text-sm transition-all"
                  >
                    <img
                      src={add}
                      alt="Increase font"
                      className="w-3 h-3 xl:w-3.5 xl:h-3.5 dark:invert flex-shrink-0"
                    />
                    <span>Increase Font</span>
                  </button>
                </div>
              )}
            </div>
            <CompanionAgentHeaderSettings />
          </div>
        </div>

        {/* Desktop patient row: Radical = compact strip. CRE = full editable grid. */}
        {patientDetailsHidden ? null : !isCreAuth ? (
          <div className="flex flex-row items-center w-full my-1 xl:my-1.5 overflow-x-auto min-w-0 gap-4 flex-wrap">
            {patientInfo.map((info, index) => (
              <div
                key={index}
                className="flex items-baseline gap-1.5 flex-shrink-0 min-w-0"
              >
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide whitespace-nowrap">
                  {info.label}
                </span>
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[140px] xl:max-w-[180px] ${
                      isLoading ? "opacity-60 italic" : ""
                    }`}
                    style={{
                      fontSize: `clamp(12px, ${fontSize}px, ${MAX_FONT_SIZE}px)`,
                    }}
                  >
                    {info.value ?? "—"}
                  </span>
                  {info.field === "accessionNo" && (
                    <button
                      type="button"
                      onClick={handleShowDsr}
                      className="h-8 px-3 rounded-full bg-[#A4C639] hover:bg-[#CBEA7B80] text-[10px] sm:text-xs font-medium text-gray-800 whitespace-nowrap"
                    >
                      Show DSR
                    </button>
                  )}
                </div>
                {index < patientInfo.length - 1 && (
                  <span
                    className="text-gray-300 dark:text-gray-600 mx-1 hidden sm:inline"
                    aria-hidden
                  >
                    |
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-row items-center w-full my-1 xl:my-2 overflow-x-auto min-w-0">
            <div className="flex flex-row items-center gap-1 sm:gap-2 flex-wrap w-full min-w-0">
              {allPatientInfo.map((info, index) => (
                <div
                  key={index}
                  className="flex flex-col items-start justify-start gap-1 sm:gap-2 flex-shrink-0 min-w-0"
                  style={{ minWidth: "fit-content" }}
                >
                  <span className="text-gray-900 dark:text-gray-200 text-[10px] sm:text-xs xl:text-sm 2xl:text-sm whitespace-nowrap">
                    {info.label}:
                  </span>
                  {isCreAuth ? (
                    info.field === "dateOfBirth" ? (
                      <input
                        type="date"
                        dir={textDirection}
                        className="rounded-md px-1.5 sm:px-2 py-0.5 sm:py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 font-bold focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-150 shadow-sm text-[10px] sm:text-xs min-w-[120px]"
                        style={{
                          fontSize: `clamp(10px, ${fontSize}px, ${MAX_FONT_SIZE}px)`,
                          width: "auto",
                          maxWidth: `${Math.max(120, fontSize * 8)}px`,
                        }}
                        value={patientDetails[info.field] || ""}
                        onChange={(e) =>
                          handleFieldChange(info.field, e.target.value)
                        }
                      />
                    ) : info.field === "patientSex" ? (
                      <select
                        dir={textDirection}
                        className="rounded-md px-1.5 sm:px-2 py-0.5 sm:py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 font-bold focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-150 shadow-sm text-[10px] sm:text-xs min-w-[70px]"
                        style={{
                          fontSize: `clamp(10px, ${fontSize}px, ${MAX_FONT_SIZE}px)`,
                          width: "auto",
                          maxWidth: `${Math.max(70, fontSize * 5)}px`,
                        }}
                        value={patientDetails[info.field] || ""}
                        onChange={(e) =>
                          handleFieldChange(info.field, e.target.value)
                        }
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="M">M</option>
                        <option value="F">F</option>
                      </select>
                    ) : info.field === "weight" ? (
                      <input
                        type="number"
                        min="0"
                        step="any"
                        dir={textDirection}
                        className="rounded-md px-1.5 sm:px-2 py-0.5 sm:py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 font-bold focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-150 shadow-sm text-[10px] sm:text-xs min-w-[60px]"
                        style={{
                          fontSize: `clamp(10px, ${fontSize}px, ${MAX_FONT_SIZE}px)`,
                          width: "auto",
                          maxWidth: `${Math.max(60, fontSize * 4)}px`,
                        }}
                        value={patientDetails[info.field] || ""}
                        onChange={(e) =>
                          handleFieldChange(info.field, e.target.value)
                        }
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          dir={textDirection}
                          className="rounded-md px-1.5 sm:px-2 py-0.5 sm:py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 font-bold focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-150 shadow-sm text-[10px] sm:text-xs"
                          style={{
                            fontSize: `clamp(10px, ${fontSize}px, ${MAX_FONT_SIZE}px)`,
                            width: "auto",
                            minWidth:
                              info.field === "insuranceName" ||
                              info.field === "insuranceNumber"
                                ? "180px"
                                : "100px",
                            maxWidth:
                              info.field === "insuranceName" ||
                              info.field === "insuranceNumber"
                                ? `${Math.max(180, fontSize * 10)}px`
                                : `${Math.max(100, fontSize * 7)}px`,
                          }}
                          value={patientDetails[info.field] || ""}
                          onChange={(e) =>
                            handleFieldChange(info.field, e.target.value)
                          }
                        />
                        {info.field === "accessionNo" && (
                          <button
                            type="button"
                            onClick={handleShowDsr}
                            className="h-8 px-3 rounded-full bg-[#A4C639] hover:bg-[#CBEA7B80] text-[10px] sm:text-xs font-medium text-gray-800 whitespace-nowrap"
                          >
                            Show DSR
                          </button>
                        )}
                      </div>
                    )
                  ) : (
                    <span
                      className={`font-bold text-[10px] sm:text-xs break-words ${
                        isLoading ? "opacity-60 italic" : ""
                      }`}
                      style={{
                        fontSize: `clamp(10px, ${fontSize}px, ${MAX_FONT_SIZE}px)`,
                        maxWidth: "none",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {info.value}
                    </span>
                  )}
                  {index < allPatientInfo.length - 1 && !isCreAuth && (
                    <h1 className="text-gray-900 dark:text-gray-200 text-xs sm:text-sm flex-shrink-0 hidden sm:block">
                      -
                    </h1>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </CreOrganisationHeaderProvider>
  );
}

export default Navbar;
